/* Admin · Users — who can sign in, what they can reach, and why.
 *
 * The table answers three questions at a glance: who is this person, what can they open
 * (role pills derived on the server from their groups), and are they active. Editing is
 * one modal that changes department and role together, because in practice they move
 * together — a person transfers, and both change.
 *
 * There is no password column and no reset action: this product has no password field
 * anywhere by design (KA-09). Sign-in is Entra ID pass-through, so credentials — and their
 * resets — belong to the tenant, not to this console.
 *
 * Two roles exist, employee and admin, so the role control is a single checkbox.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'

import { users } from '../../api/client'
import type { DirectoryUser, DirectoryUserList } from '../../api/types'
import {
  Card,
  EmptyState,
  Kpi,
  PageHead,
  Skeleton,
  dateHuman,
  errorMessage,
  initialsOf,
  nf,
  useConfirm,
  useModal,
  useToast,
} from '../../ui/chrome'
import { Icon } from '../../ui/icons'
import { exportCSV } from '../../ui/pdf'

/* The group vocabulary this deployment understands. Department groups grant document
 * access; the admin group opens the console. Kept here rather than fetched because they
 * mirror ADMIN_GROUP_IDS in server config — the server is still the authority, and it
 * recomputes the role flag on every write. */
const DEPARTMENT_GROUPS: { id: string; label: string }[] = [
  { id: 'grp-all-employees', label: 'All employees (baseline)' },
  { id: 'grp-hr', label: 'Human Resources' },
  { id: 'grp-it', label: 'Information Technology' },
  { id: 'grp-finance', label: 'Finance' },
  { id: 'grp-ops', label: 'Operations' },
]

/** The one group that turns an employee into an admin. */
const ADMIN_GROUP = 'grp-app-admin'

const DEPARTMENTS = [
  'Human Resources',
  'Information Technology',
  'Finance',
  'Operations',
  'Corporate',
  'External',
]

function roleLabel(user: DirectoryUser): string {
  return user.is_admin ? 'Admin' : 'Employee'
}

function RolePill({ user }: { user: DirectoryUser }) {
  return (
    <span className={`pill ${user.is_admin ? 'p-purple' : 'p-grey'}`}>{roleLabel(user)}</span>
  )
}

export default function UsersScreen({ currentUpn }: { currentUpn: string }) {
  const toast = useToast()
  const confirm = useConfirm()
  const { openModal, closeModal } = useModal()

  const [data, setData] = useState<DirectoryUserList | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [dept, setDept] = useState('')
  const [role, setRole] = useState('')
  const [activeOnly, setActiveOnly] = useState(false)

  const refresh = useCallback(() => {
    users
      .list()
      .then((response) => {
        setData(response)
        setError(null)
      })
      .catch((cause: unknown) => setError(errorMessage(cause)))
  }, [])

  useEffect(refresh, [refresh])

  const rows = useMemo(() => {
    let items = data?.items ?? []
    if (q) {
      const needle = q.toLowerCase()
      items = items.filter((u) =>
        [u.display_name, u.upn, u.job_title ?? '', u.department ?? '']
          .some((v) => v.toLowerCase().includes(needle)),
      )
    }
    if (dept) items = items.filter((u) => u.department === dept)
    if (role === 'admin') items = items.filter((u) => u.is_admin)
    if (role === 'employee') items = items.filter((u) => !u.is_admin)
    if (activeOnly) items = items.filter((u) => u.is_active)
    return items
  }, [data, q, dept, role, activeOnly])

  const departments = useMemo(() => {
    const seen = new Set<string>()
    for (const u of data?.items ?? []) if (u.department) seen.add(u.department)
    return Array.from(seen).sort()
  }, [data])

  /* ------------------------------------------------------------ add / edit */

  function userForm(existing: DirectoryUser | null) {
    // Plain mutable draft: the modal body is rendered once, and the server is the
    // authority on what the groups mean, so local state would buy nothing here.
    const draft = {
      upn: existing?.upn ?? '',
      display_name: existing?.display_name ?? '',
      department: existing?.department ?? '',
      job_title: existing?.job_title ?? '',
      groups: new Set(existing?.entra_group_ids ?? ['grp-all-employees']),
    }

    const body = (
      <div>
        <div className="row-2">
          <div className="field">
            <label htmlFor="uName">
              Full name <span className="req">*</span>
            </label>
            <input
              id="uName"
              className="inp"
              defaultValue={draft.display_name}
              placeholder="Asha Rao"
              onChange={(e) => (draft.display_name = e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="uUpn">
              Corporate email <span className="req">*</span>
            </label>
            <input
              id="uUpn"
              className="inp"
              defaultValue={draft.upn}
              disabled={existing !== null}
              placeholder="asha.rao@kirloskar.com"
              onChange={(e) => (draft.upn = e.target.value)}
            />
            {existing && <div className="help">The sign-in identifier cannot be changed.</div>}
          </div>
        </div>

        <div className="row-2">
          <div className="field">
            <label htmlFor="uDept">Department</label>
            <select
              id="uDept"
              className="inp"
              defaultValue={draft.department}
              onChange={(e) => (draft.department = e.target.value)}
            >
              <option value="">Not set</option>
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="uTitle">Designation</label>
            <input
              id="uTitle"
              className="inp"
              defaultValue={draft.job_title}
              placeholder="HR Business Partner"
              onChange={(e) => (draft.job_title = e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label>Document access</label>
          <div className="help" style={{ marginTop: 0, marginBottom: 8 }}>
            Entra ID groups this person belongs to.
          </div>
          {DEPARTMENT_GROUPS.map((g) => (
            <label key={g.id} className="chk">
              <input
                type="checkbox"
                defaultChecked={draft.groups.has(g.id)}
                onChange={(e) => (e.target.checked ? draft.groups.add(g.id) : draft.groups.delete(g.id))}
              />
              <span>
                <strong>{g.label}</strong>
                <span className="tiny muted mono"> · {g.id}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="field">
          <label>Role</label>
          <div className="help" style={{ marginTop: 0, marginBottom: 8 }}>
            Leave this unticked for a normal employee.
          </div>
          <label className="chk">
            <input
              type="checkbox"
              defaultChecked={draft.groups.has(ADMIN_GROUP)}
              onChange={(e) =>
                e.target.checked ? draft.groups.add(ADMIN_GROUP) : draft.groups.delete(ADMIN_GROUP)
              }
            />
            <span>
              <strong>Admin</strong>
              <span className="tiny muted"> — full console access</span>
            </span>
          </label>
        </div>

      </div>
    )

    function save() {
      const name = draft.display_name.trim()
      const upn = draft.upn.trim().toLowerCase()
      if (!name) {
        toast('err', 'Name is required', 'Enter the full name of the employee.')
        return
      }
      if (!existing && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(upn)) {
        toast('err', 'Corporate email is not valid', 'Use the form name@kirloskar.com.')
        return
      }
      const groups = Array.from(draft.groups)
      if (groups.length === 0) {
        toast('err', 'At least one group is required', 'Without a group this person sees nothing.')
        return
      }
      const payload = {
        display_name: name,
        department: draft.department || null,
        job_title: draft.job_title.trim() || null,
        entra_group_ids: groups,
      }
      const action = existing
        ? users.update(existing.id, payload)
        : users.create({ ...payload, upn })
      action
        .then((saved) => {
          closeModal()
          refresh()
          toast(
            'ok',
            existing ? 'User updated' : 'User added',
            `${saved.display_name} · ${roleLabel(saved)}${saved.department ? ` · ${saved.department}` : ''}`,
          )
        })
        .catch((cause: unknown) =>
          toast('err', existing ? 'Update failed' : 'Could not add the user', errorMessage(cause)),
        )
    }

    openModal({
      title: existing ? `Edit ${existing.display_name}` : 'Add a user',
      sub: existing
        ? 'Department and role changes take effect the next time they sign in.'
        : 'They appear on the sign-in screen straight away.',
      body,
      footer: (
        <>
          <button type="button" className="btn btn-soft" onClick={closeModal}>
            Cancel
          </button>
          <button type="button" className="btn btn-lime" onClick={save}>
            <Icon name="check" size={16} /> {existing ? 'Save changes' : 'Add user'}
          </button>
        </>
      ),
    })
  }

  /* --------------------------------------------------------- row actions */

  function openProfile(user: DirectoryUser) {
    openModal({
      title: user.display_name,
      sub: `${roleLabel(user)}${user.department ? ` · ${user.department}` : ''}`,
      size: 'narrow',
      body: (
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-b">
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 14 }}>
              <span className="avatar" style={{ width: 52, height: 52, fontSize: 17 }}>
                {initialsOf(user.display_name)}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{user.display_name}</div>
                <div className="tiny muted">{user.job_title ?? 'Designation not set'}</div>
                <div style={{ marginTop: 5 }}>
                  <RolePill user={user} />
                </div>
              </div>
            </div>
            {[
              ['Corporate email', user.upn],
              ['Department', user.department ?? '—'],
              ['Status', user.is_active ? 'Active' : 'Deactivated'],
              ['Added', dateHuman(user.created_at)],
            ].map(([k, v]) => (
              <div key={k} className="stat-line">
                <span className="sl-k">{k}</span>
                <span className="sl-v">{v}</span>
              </div>
            ))}
            <div className="stat-line">
              <span className="sl-k">Entra ID groups</span>
              <span className="sl-v">
                {user.entra_group_ids.length > 0
                  ? user.entra_group_ids.map((g) => (
                      <span key={g} className="tag grp">
                        {g}
                      </span>
                    ))
                  : 'None — refused at sign-in'}
              </span>
            </div>
            {user.scenario_note && (
              <div className="alert a-grey" style={{ marginTop: 12, marginBottom: 0 }}>
                <Icon name="info" size={18} />
                <div>{user.scenario_note}</div>
              </div>
            )}
          </div>
        </div>
      ),
      footer: (
        <>
          <button type="button" className="btn btn-soft" onClick={closeModal}>
            Close
          </button>
          <button
            type="button"
            className="btn btn-lime"
            onClick={() => {
              closeModal()
              userForm(user)
            }}
          >
            <Icon name="edit" size={16} /> Edit
          </button>
        </>
      ),
    })
  }

  function toggleActive(user: DirectoryUser) {
    const deactivating = user.is_active
    const apply = () =>
      users
        .update(user.id, { is_active: !user.is_active })
        .then(() => {
          refresh()
          toast(
            'ok',
            deactivating ? 'User deactivated' : 'User reactivated',
            deactivating
              ? `${user.display_name} can no longer sign in. Their history is kept.`
              : `${user.display_name} can sign in again.`,
          )
        })
        .catch((cause: unknown) => toast('err', 'Change failed', errorMessage(cause)))

    if (deactivating) {
      confirm(
        'Deactivate this user?',
        `${user.display_name} will disappear from the sign-in screen and cannot use the assistant. ` +
          'Their conversations and audit history are kept, and you can reactivate them at any time.',
        'Deactivate',
        apply,
        true,
      )
    } else {
      apply()
    }
  }

  function removeUser(user: DirectoryUser) {
    if (user.upn.toLowerCase() === currentUpn.toLowerCase()) {
      toast('warn', 'You cannot delete your own account', 'Ask another administrator to do it.')
      return
    }
    confirm(
      'Delete this user permanently?',
      `${user.display_name} (${user.upn}) will be removed from the directory. Deactivating instead ` +
        'keeps their identity attached to past audit entries — prefer that unless the account was created in error.',
      'Delete permanently',
      () => {
        users
          .remove(user.id)
          .then(() => {
            refresh()
            toast('ok', 'User deleted', user.display_name)
          })
          .catch((cause: unknown) => toast('err', 'Delete failed', errorMessage(cause)))
      },
      true,
    )
  }

  /* ---------------------------------------------------------------- render */

  return (
    <>
      <PageHead
        title="Users"
        actions={
          <>
            <button
              type="button"
              className="btn btn-soft"
              disabled={!data || data.items.length === 0}
              onClick={() => {
                exportCSV('kbl-users.csv', [
                  ['Name', 'Corporate email', 'Department', 'Designation', 'Role', 'Status', 'Entra groups', 'Added'],
                  ...(data?.items ?? []).map((u) => [
                    u.display_name,
                    u.upn,
                    u.department ?? '',
                    u.job_title ?? '',
                    roleLabel(u),
                    u.is_active ? 'Active' : 'Deactivated',
                    u.entra_group_ids.join(' | '),
                    u.created_at,
                  ]),
                ])
                toast('ok', 'Export complete', `kbl-users.csv downloaded (${data?.total ?? 0} rows).`)
              }}
            >
              <Icon name="dl" size={16} /> Export CSV
            </button>
            <button type="button" className="btn btn-lime" onClick={() => userForm(null)}>
              <Icon name="plus" size={16} /> Add user
            </button>
          </>
        }
      />

      <div className="grid g4 mb16">
        <Kpi label="Total users" value={data ? nf(data.total) : '—'} desc="in the directory" />
        <Kpi
          tone="green"
          label="Active"
          value={data ? nf(data.active) : '—'}
          desc="can sign in today"
        />
        <Kpi
          tone="purple"
          label="Admins"
          value={data ? nf(data.admins) : '—'}
          desc="full console access"
        />
        <Kpi
          tone="info"
          label="Departments"
          value={nf(departments.length)}
          desc="represented in the directory"
        />
      </div>

      <div className="filters">
        <div className="field">
          <label htmlFor="uQ">Search</label>
          <input
            id="uQ"
            className="inp"
            placeholder="Name, email or designation"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="uDeptF">Department</label>
          <select id="uDeptF" className="inp" value={dept} onChange={(e) => setDept(e.target.value)}>
            <option value="">All</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="uRoleF">Role</label>
          <select id="uRoleF" className="inp" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">All</option>
            <option value="admin">Admin</option>
            <option value="employee">Employee</option>
          </select>
        </div>
        <div className="field" style={{ flex: '0 0 auto' }}>
          <label htmlFor="uActive">Status</label>
          <select
            id="uActive"
            className="inp"
            value={activeOnly ? 'active' : ''}
            onChange={(e) => setActiveOnly(e.target.value === 'active')}
          >
            <option value="">All</option>
            <option value="active">Active only</option>
          </select>
        </div>
        <button
          type="button"
          className="btn btn-soft"
          onClick={() => {
            setQ('')
            setDept('')
            setRole('')
            setActiveOnly(false)
            toast('info', 'Filters cleared')
          }}
        >
          <Icon name="refresh" size={16} /> Reset
        </button>
      </div>

      {error && (
        <div className="alert a-danger">
          <Icon name="alert" size={18} />
          <div>
            <strong>The user list could not be loaded.</strong>
            {error}
          </div>
        </div>
      )}

      <Card
        icon="users"
        title="Directory"
        hint={data ? `${rows.length} of ${nf(data.total)}` : undefined}
        tight
      >
        {!data && !error ? (
          <Skeleton rows={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="users"
            title="No users match"
            msg="Adjust or clear the filters."
            action={
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setQ('')
                  setDept('')
                  setRole('')
                  setActiveOnly(false)
                }}
              >
                Clear filters
              </button>
            }
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Role</th>
                  <th>Access groups</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((user) => (
                  <tr key={user.id} style={user.is_active ? undefined : { opacity: 0.62 }}>
                    <td>
                      <div className="flex aic gap8">
                        <span
                          className="avatar"
                          style={{ width: 34, height: 34, fontSize: 12, flexShrink: 0 }}
                        >
                          {initialsOf(user.display_name)}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <strong>{user.display_name}</strong>
                          {user.upn.toLowerCase() === currentUpn.toLowerCase() && (
                            <span className="pill p-lime" style={{ marginLeft: 6 }}>
                              You
                            </span>
                          )}
                          <div className="tiny muted mono">{user.upn}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {user.department ? (
                        <span className="pill p-grey">{user.department}</span>
                      ) : (
                        <span className="tiny muted">Not set</span>
                      )}
                      {user.job_title && <div className="tiny muted">{user.job_title}</div>}
                    </td>
                    <td>
                      <RolePill user={user} />
                    </td>
                    <td>
                      {user.entra_group_ids.length > 0 ? (
                        user.entra_group_ids
                          .filter((g) => !g.startsWith('grp-app-') && g !== 'grp-hil-reviewer')
                          .map((g) => (
                            <span key={g} className="tag grp">
                              {g}
                            </span>
                          ))
                      ) : (
                        <span className="pill p-danger">No groups — refused at sign-in</span>
                      )}
                    </td>
                    <td>
                      <span className={`pill ${user.is_active ? 'p-ok' : 'p-grey'}`}>
                        {user.is_active ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          type="button"
                          className="btn-icon"
                          title="View profile"
                          onClick={() => openProfile(user)}
                        >
                          <Icon name="user" size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title="Change department or role"
                          onClick={() => userForm(user)}
                        >
                          <Icon name="edit" size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title={user.is_active ? 'Deactivate' : 'Reactivate'}
                          onClick={() => toggleActive(user)}
                        >
                          <Icon name={user.is_active ? 'lock' : 'check'} size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          title="Delete permanently"
                          onClick={() => removeUser(user)}
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  )
}
